import { History, UnregisterCallback } from 'history'
import { action, computed, makeObservable, observable } from 'mobx'

// Structural so the guard has no dependency on the session store module.
export interface IGuardedDrawingSession {
  // True while a discard would lose draft work; false when idle or while a
  // submit is in flight (the drafts belong to the create).
  hasGuardedWork: boolean
  endCreation(outcome: 'discard' | 'complete'): void
}

// While drawn-but-unsaved markups exist, every loss surface (navigation,
// view/map switches, create, unload) must confirm before discarding (STX1-686).
export default class WorkflowDrawingGuardStore {
  @observable public isConfirmShown: boolean = false
  @observable.ref private session: IGuardedDrawingSession = null
  private pendingResolve: (confirmed: boolean) => void = null
  private unblockHistory: UnregisterCallback = null

  public constructor() {
    makeObservable(this)
  }

  @computed
  public get isBlocked(): boolean {
    return !!this.session?.hasGuardedWork
  }

  public register(session: IGuardedDrawingSession, history?: History): void {
    this.unregister()
    this.setSession(session)
    if (typeof history?.block === 'function') {
      this.unblockHistory = history.block((location, blockedAction) => {
        if (!this.isBlocked) {
          return undefined
        }
        void this.confirm().then(confirmed => {
          if (!confirmed) {
            return
          }
          this.session?.endCreation('discard')
          const target = location.pathname + location.search + location.hash
          // POP covers Back AND Forward with no direction flag, so replaying it as
          // goBack() would navigate the wrong way on Forward. Go to the blocked
          // target instead; replace (not push) keeps the abandoned entry from
          // lingering as a duplicate forward one.
          if (blockedAction === 'POP' || blockedAction === 'REPLACE') {
            history.replace(target)
          } else {
            history.push(target)
          }
        })
        // Block synchronously; the confirmed navigation is replayed above.
        return false
      })
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  }

  public unregister(): void {
    this.setSession(null)
    this.unblockHistory?.()
    this.unblockHistory = null
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    this.resolveConfirm(false)
  }

  /** Runs the action, confirming + discarding the session first if work is at risk. */
  public guard = (proceed: () => void): void => {
    if (!this.isBlocked) {
      return proceed()
    }
    void this.confirm().then(confirmed => {
      if (!confirmed) {
        return
      }
      this.session?.endCreation('discard')
      proceed()
    })
  }

  @action.bound
  public onDialogConfirm(): void {
    this.resolveConfirm(true)
  }

  @action.bound
  public onDialogCancel(): void {
    this.resolveConfirm(false)
  }

  private confirm(): Promise<boolean> {
    // A dialog is already up — first caller wins; a racing second guarded action is a
    // no-op rather than superseding and dropping the first caller's navigation.
    if (this.pendingResolve) {
      return Promise.resolve(false)
    }
    const promise = new Promise<boolean>(resolve => {
      this.pendingResolve = resolve
    })
    this.showConfirm()
    return promise
  }

  @action
  private setSession(session: IGuardedDrawingSession): void {
    this.session = session
  }

  @action
  private showConfirm(): void {
    this.isConfirmShown = true
  }

  @action
  private resolveConfirm(confirmed: boolean): void {
    this.isConfirmShown = false
    const resolve = this.pendingResolve
    this.pendingResolve = null
    resolve?.(confirmed)
  }

  private handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (!this.isBlocked) {
      return
    }
    event.preventDefault()
    event.returnValue = ''
  }
}
